import { settingsAPI } from '../services/api';
import { CV_REPORT_PROFILES } from './cvDocumentReport';

// Per-report header / footer settings (Settings → App Configuration → Reports &
// Documents). Every printed and PDF report looks up its profile by key; any
// field left blank falls back to the "All reports" default, then to the older
// single global report_* settings, so nothing changes until someone edits it.
export const REPORTS = [
  { key: 'default', label: 'All reports (default)' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'api_master', label: 'API Master' },
  { key: 'cleanability_assessment', label: 'Cleanability Assessment' },
  { key: 'product_master', label: 'Product Master' },
  { key: 'equipment_master', label: 'Equipment Master' },
  { key: 'equipment_train', label: 'Equipment Train' },
  { key: 'cleaning_config', label: 'Cleaning Config' },
  { key: 'acceptance_criteria', label: 'Acceptance Criteria' },
  { key: 'sampling_plan', label: 'Sampling Plan' },
  { key: 'maco', label: 'MACO Calculation' },
  { key: 'worst_case', label: 'Worst Case Analysis' },
  { key: 'risk_assessment', label: 'Risk Assessment' },
  { key: 'impact_assessment', label: 'Impact Assessment' },
  { key: 'gap_assessment', label: 'Gap Assessment' },
  { key: 'audit_trail', label: 'Audit Trail' },
  { key: 'ai_document', label: 'AI-drafted Documents' },
  ...CV_REPORT_PROFILES,
];

export const PROFILE_FIELDS = [
  { key: 'document_name', label: 'Document name (header)', placeholder: 'e.g. Product Master Report' },
  { key: 'format_no', label: 'Format No.', placeholder: 'e.g. QA-SOP-011-F-01-00' },
  { key: 'revision_no', label: 'Revision No.', placeholder: 'e.g. 01' },
  { key: 'effective_date', label: 'Effective date', placeholder: 'e.g. 01-Nov-2026' },
  { key: 'footer_text', label: 'Footer text', placeholder: 'e.g. Confidential — for internal use only' },
];

export const SETTING_KEY = 'report_profiles';
let cache = { profiles: {}, legacy: {} };

const parse = (raw) => {
  try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
};

export const setReportSettingsCache = (settings = {}) => {
  cache = { profiles: parse(settings[SETTING_KEY]), legacy: settings };
};

export const loadReportProfiles = async () => {
  try {
    const res = await settingsAPI.getAll();
    setReportSettingsCache(res?.data?.data || {});
  } catch { /* keep the previous cache */ }
  return cache;
};

export const getStoredProfiles = () => cache.profiles;

const pick = (...values) => values.find((v) => v !== undefined && v !== null && String(v).trim() !== '');

// Address of the Unit/Plant selected in the header (App Configuration →
// Company & Units), or '' when "All Units" is selected or it has none.
export const activeUnitAddress = () => {
  try {
    const code = String(localStorage.getItem('activePlant') || '').toLowerCase();
    const plants = JSON.parse(localStorage.getItem('availablePlants') || '[]');
    const unit = Array.isArray(plants) ? plants.find((p) => String(p.plant_code || '').toLowerCase() === code) : null;
    return unit?.address || '';
  } catch {
    return '';
  }
};

// Address for a report header: the selected unit's own address, otherwise the
// report client address setting.
export const unitReportAddress = (settings = cache.legacy) => pick(activeUnitAddress(), settings?.report_client_address, '');

// Effective header/footer for one report.
export const getReportProfile = (reportKey) => {
  const own = cache.profiles[reportKey] || {};
  const def = cache.profiles.default || {};
  const legacy = cache.legacy;
  return {
    document_name: pick(own.document_name, ''),
    format_no: pick(own.format_no, legacy[`report_format_no_${reportKey}`], def.format_no, ''),
    revision_no: pick(own.revision_no, def.revision_no, legacy.report_document_rev_no, '01'),
    effective_date: pick(own.effective_date, def.effective_date, ''),
    footer_text: pick(own.footer_text, def.footer_text, legacy.report_footer_tagline, ''),
    client_name: pick(legacy.report_client_name, ''),
    client_address: unitReportAddress(legacy),
    client_logo: legacy.report_client_logo || '',
    primeclean_logo: legacy.report_primeclean_logo || '',
    show_logos: own.show_logos !== false,
  };
};

// The older report_* settings object, with this report's profile applied —
// for exports that already draw their own header/footer from those keys.
export const withReportProfile = (reportKey, fallbackDocumentName = '') => {
  const p = getReportProfile(reportKey);
  return {
    ...cache.legacy,
    report_document_name: pick(p.document_name, cache.legacy.report_document_name, fallbackDocumentName),
    report_document_rev_no: p.revision_no,
    report_footer_tagline: pick(p.footer_text, cache.legacy.report_footer_tagline),
    report_client_address: p.client_address,
    [`report_format_no_${reportKey}`]: p.format_no,
    report_client_logo: p.show_logos ? cache.legacy.report_client_logo : '',
    report_primeclean_logo: p.show_logos ? cache.legacy.report_primeclean_logo : '',
  };
};

// Footer for jsPDF exports: Format No / Rev / effective date, the footer text,
// and "Page x of y" on every page. Call just before doc.save().
export const stampPdfFooter = (doc, reportKey, { pageNumbers = true } = {}) => {
  if (!doc?.internal) return;
  const p = getReportProfile(reportKey);
  const pages = doc.internal.getNumberOfPages();
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const left = [p.format_no && `Format No: ${p.format_no}`, p.revision_no && `Rev: ${p.revision_no}`, p.effective_date && `Effective: ${p.effective_date}`].filter(Boolean).join('   |   ');
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100);
    if (left) doc.text(left, 24, height - 18);
    if (p.footer_text) doc.text(String(p.footer_text).slice(0, 140), width / 2, height - 8, { align: 'center' });
    if (pageNumbers) doc.text(`Page ${i} of ${pages}`, width - 24, height - 18, { align: 'right' });
  }
};
